import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { FC, SyntheticEvent, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useAppStore } from '../../../app/api/app-api';
import { useAppOwners } from '../../../application-owners/api/applications-owners-api';
import ApplicationIrrelevant from '../../../applications/components/ApplicationIrrelevant';
import AppSpinner from '../../../common/components/AppSpinner';
import EmptyPageAvatar from '../../../common/components/EmptyPageAvatar';
import { AppPages } from '../../../common/routing/app-navigator';
import SidebarLayout from '../../../elements/SidebarLayout/SidebarLayout';
import VerticalSplitPane from '../../../elements/VerticalSplitPane/VerticalSplitPane';
import {
  resetJiraTicketForm,
  setValidationObject,
} from '../../../jira/store-actions/jira-ticket-store-actions';
import {
  useGradualUpdate,
  useScan,
} from '../../../new-scan/actions/scan-actions';
import FilesDiffModalContainer from '../../../quick-fix/containers/FilesDiffModalContainer';
import PermissionDeniedModalContainer from '../../../quick-fix/containers/PermissionDeniedModalContainer';
import QuickFixModalContainer from '../../../quick-fix/containers/QuickFixModalContainer';
import {
  initIssues,
  loadFilters,
  loadIssues,
  sortIssues,
} from '../../active-issues/actions/active-issues-actions';
import { setInitialFilters } from '../../active-issues/actions/filter-issues-actions';
import {
  clearStore,
  setApplicationIrrelevantModal,
  setExclusionAppComment,
  setFilesDiffModalVisibility,
  setQuickFixModalVisibility,
  setQuickFixPermissionDeniedModalVisibility,
  setShowCreateJiraTicketDialog,
  setShowSendSlackMessageDialog,
  setPolicyFixModal,
} from '../../active-issues/store-actions/issue-store-actions';
import IssuesStore from '../../active-issues/stores/issues-store';
import {
  onIrrelevantApp,
  useIssuesExclusions,
} from '../actions/issues-exclusions-actions';
import PolicyFixModal from '../components/IssueItem/PolicyFix/PolicyFixModal';
import IssuesTable from '../components/IssuesTable/IssuesTable';
import CreateJiraTicketModalContainer from './CreateJiraTicketModaContainer';
import DisablePolicyContainer from './DisablePolicyContainer';
import ExclusionIssueContainer from './ExclusionIssueContainer';
import IssueCommentContainer from './IssueCommentContainer';
import IssueDrawerContainer from './IssueDrawerContainer';
import IssuesSidebarContainer from './IssuesSidebarContainer';
import SendSlackMessageModalContainer from './SendSlackMessageModalContainer';
import { isDevelopment } from 'env-utils';
import IssuesControlsContainer from './IssuesControlsContainer';

const IssuesContainer: FC<React.PropsWithChildren<IssuesContainerProps>> = ({
  navigateToConnectors,
}) => {
  const {
    showCreateJiraTicketDialog,
    showSendSlackMessageDialog,
    isQuickFixModalVisible,
    isQuickFixPermissionDeniedModalVisible,
    isFilesDiffModalVisible,
    isPolicyFixOpen,
    loading,
    issues,
    selectedIssue,
    order,
    orderBy,
    applicationIrrelevantModal,
    selectedIssueId,
  } = useSnapshot(IssuesStore);

  const { selectedAggs } = useIssuesExclusions();
  const [searchParams] = useSearchParams();
  const filters = searchParams.get('filters');
  const limit = searchParams.get('limit');
  const unit = searchParams.get('unit');
  const { displaySelectedAppOwners } = useAppOwners();
  const { isEmptyOrg } = useAppStore();

  const { isCancelScanLoading } = useScan();
  const { inProgress } = useGradualUpdate(
    useCallback(() => {
      setInitialFilters(filters);
      loadIssues({ update: true, cache: false });
      loadFilters({ cache: false });
    }, [filters]),
  );

  useEffect(() => {
    setInitialFilters(filters);
    loadIssues({ update: true });
    loadFilters();
  }, [displaySelectedAppOwners, filters, unit, limit]);

  useEffect(() => {
    if (!filters) initIssues();
  }, [filters]);

  useEffect(() => {
    return clearStore;
  }, []);

  const handleCloseIrrelevantApp = () => {
    setApplicationIrrelevantModal(false);
  };

  const handleClickConnectors = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      navigateToConnectors();
    },
    [navigateToConnectors],
  );

  const onCloseJiraTicketModal = useCallback(() => {
    setShowCreateJiraTicketDialog(false);
    resetJiraTicketForm();
    setValidationObject(null);
  }, []);

  const onCloseSendSlackMessageModal = useCallback(() => {
    setShowSendSlackMessageDialog(false);
  }, []);

  const onCloseQuickFix = useCallback(() => {
    setQuickFixModalVisibility(false);
  }, []);

  const onCloseQuickFixPermissionDenied = useCallback(() => {
    setQuickFixPermissionDeniedModalVisibility(false);
  }, []);

  const onCloseFilesDiffModal = useCallback(() => {
    setFilesDiffModalVisibility(false);
  }, []);

  const onShowFilesDiffActionButtonClick = useCallback((): void => {
    setQuickFixModalVisibility(true);
    setFilesDiffModalVisibility(false);
  }, []);

  const onViewCodeFixClick = useCallback((): void => {
    setQuickFixModalVisibility(false);
    setFilesDiffModalVisibility(true);
  }, []);

  const onClosePolicyFix = useCallback(() => {
    setPolicyFixModal(false);
  }, []);

  if (isCancelScanLoading) {
    return <AppSpinner text='Canceling scan. Please wait...' />;
  }

  if ((loading.isIdle() || loading.isPending()) && issues.length === 0) {
    return <AppSpinner text='Loading Issues...' />;
  }

  if (inProgress && issues.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if (loading.isError()) {
    return (
      <Alert severity='error'>
        <AlertTitle>Error</AlertTitle>
        <Typography variant='body1'>An error has occurred</Typography>
      </Alert>
    );
  }

  if (isEmptyOrg && !inProgress) {
    return (
      <EmptyPageAvatar
        text={
          <>
            Please run a scan to discover application security risks in your
            organization. To run a scan...
            <Typography
              variant='body1'
              color='text.primary'
              component='a'
              onClick={handleClickConnectors}
              href={AppPages.Connectors}
              mt={1}
              style={{ display: 'block', fontWeight: 'bold', color: '#FFF' }}>
              Connect your Git projects
            </Typography>
          </>
        }
      />
    );
  }

  return (
    <SidebarLayout aside={<IssuesSidebarContainer />}>
      <Stack direction='column' height='100%' sx={{ overflowY: 'auto' }}>
        <VerticalSplitPane
          content={
            <>
              {issues.length && isDevelopment() && <IssuesControlsContainer />}
              {issues.length === 0 && (
                <EmptyPageAvatar
                  text={
                    <Typography
                      variant='body1'
                      color='white'
                      textAlign='center'>
                      No results found
                      {filters && (
                        <>
                          <br />
                          Try removing some filters to show more results
                        </>
                      )}
                    </Typography>
                  }
                />
              )}
              {issues.length > 0 && (
                <>
                  <IssuesTable
                    issues={issues}
                    selectedID={selectedIssueId}
                    order={order[0]}
                    orderBy={orderBy[0]}
                    onSort={sortIssues}
                    loading={loading.isPending()}
                  />
                </>
              )}
            </>
          }>
          {({ className }) => <IssueDrawerContainer className={className} />}
        </VerticalSplitPane>
      </Stack>
      <DisablePolicyContainer />
      <ApplicationIrrelevant
        applicationIrrelevantModal={applicationIrrelevantModal}
        onIrrelevantApp={onIrrelevantApp}
        appName={selectedIssue?.app.name}
        appId={selectedIssue?.app.id}
        handleClose={handleCloseIrrelevantApp}
        excludeComment={selectedIssue?.exclusionAppComment}
        setExcludeComment={setExclusionAppComment}
      />
      <ExclusionIssueContainer />
      {selectedIssue && (
        <CreateJiraTicketModalContainer
          selectedIssue={selectedIssue}
          onClose={onCloseJiraTicketModal}
          open={showCreateJiraTicketDialog}
        />
      )}
      {isDevelopment() && selectedIssue && (
        <SendSlackMessageModalContainer
          selectedIssue={selectedIssue}
          onClose={onCloseSendSlackMessageModal}
          open={showSendSlackMessageDialog}
        />
      )}
      {isQuickFixModalVisible && (
        <QuickFixModalContainer
          selectedIssue={selectedIssue}
          selectedAggs={selectedAggs}
          onClose={onCloseQuickFix}
          open={isQuickFixModalVisible}
          onViewCodeFix={onViewCodeFixClick}
        />
      )}
      {isQuickFixPermissionDeniedModalVisible && (
        <PermissionDeniedModalContainer
          onClose={onCloseQuickFixPermissionDenied}
          selectedIssue={selectedIssue}
          open={isQuickFixPermissionDeniedModalVisible}
        />
      )}
      {isFilesDiffModalVisible && (
        <FilesDiffModalContainer
          open={isFilesDiffModalVisible}
          selectedAggs={selectedAggs}
          selectedIssue={selectedIssue}
          onClose={onCloseFilesDiffModal}
          onActionButtonClick={onShowFilesDiffActionButtonClick}
        />
      )}
      {isPolicyFixOpen && selectedIssue?.fixes && (
        <PolicyFixModal
          fix={selectedIssue.fixes}
          open={isPolicyFixOpen}
          onClose={onClosePolicyFix}
        />
      )}
      <IssueCommentContainer />
    </SidebarLayout>
  );
};

interface IssuesContainerProps {
  navigateToConnectors(): void;
}

export default IssuesContainer;
